import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';

import { useNavigate } from 'react-router-dom';
import ErrorState from '../../../components/states/ErrorState';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { CoachDto } from '../../../models/coach/dto/coach.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useListCoachs } from '../hooks/useListCoachs';
import CoachActionMenu from './CoachActionMenu';
import { useIsOrgManager } from '../../auth/roles';
import CreateCoachForm from './CreateCoachForm';
import InviteCoachForm from './InviteCoachForm';

const columnHelper = createColumnHelper<CoachDto>();

export default function CoachList() {
    const isOrgManager = useIsOrgManager();
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError } = useListCoachs(selectedOrganization?.id);
    // `enabled: Boolean(organizationId)`. `showLoader` used to be
    // `(isLoading || isFetching) && !hasData`, and for a DISABLED query
    // `isLoading` is `true` with `isFetching` false — so it spun forever under
    // an empty table header. Swapping it for `isInitialLoading` alone would
    // have dropped straight into the `!hasData` arm below, which says
    // `no_data`: "this organization has no coaches", about a read never sent.
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const navigation = useNavigate();

    const columns = useMemo(() => [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    ID
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('firstName', {
            id: 'firstName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('coachs.firstName')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('lastName', {
            id: 'lastName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('coachs.lastName')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('sports', {
            id: 'sports',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.sport')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        initialState: {
            columnVisibility: {
                id: false
            }
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const hasData = (data || []).length > 0;
    const showLoader = readState === 'loading';
    const isNeverIssued = readState === 'never-issued';

    return (
        <PageCard title={translate('tabs.coachs')} rightHeader={isOrgManager ? <><CreateCoachForm />{/* TODO: Re-enable this */}{false && <InviteCoachForm />}</> : null}>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr
                                key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                                {/* Coaches get no row actions, so the column
                                    itself is dropped rather than rendered
                                    empty (it must match the Td below). */}
                                {isOrgManager && <Th key={'actions'}
                                    width={10}
                                    {...tableThProps}
                                    borderColor={borderColor}
                                    cursor='pointer'>

                                </Th>}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}
                                    {...tableRowProps}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                onClick={() => {
                                                    navigation(`/admin/coachDetails?coach=${row.getValue('id')}`);
                                                }}
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                    {isOrgManager && <Td>
                                        <CoachActionMenu coachId={row.getValue('id')} hasDashboardAccess={row.original.hasDashboardAccess} />
                                    </Td>}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                <Loader isLoading={showLoader} />
                {isNeverIssued && <ScopePrompt gate='organization' />}
                {!showLoader && isError && (
                    <ErrorState message={translate('coachs.error_loading')} />
                )}
                {!showLoader && !isNeverIssued && !isError && !hasData && (
                    <Flex justify='center' py={10}>
                        <Text color='secondaryGray.600' fontSize='sm'>
                            {translate('no_data')}
                        </Text>
                    </Flex>
                )}
            </Box>
        </PageCard>
    );
}

export const dateToDay = (date: Date) => {
    const userLocale = navigator.language || 'en';
    const formatter = new Intl.DateTimeFormat(userLocale, { weekday: 'short', day: 'numeric', month: 'short' });
    return formatter.format(date);
};
